import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductionValueStep } from 'models/organization';
import { useProductionValueSteps } from 'hooks/useProductionValueSteps';
import { useDepartments } from 'context/departments-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { Department } from 'models/organization';
import { useWorkflowSteps } from 'context/workflow-steps-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import {
    LinkableWorkflowStep,
    isWorkflowValueLinkEnabled,
    linkableWorkflowSteps,
} from 'utils/production-value-step-links';

type Props = {
    open: boolean;
    step?: ProductionValueStep;
    onDialogClose: () => void;
};

const AddEditProductionValueStepDialog: FC<Props> = ({ open, step, onDialogClose }) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { departments } = useDepartments();
    const { addProductionValueStep, updateProductionValueStep, addStepLoading, editStepLoading } =
        useProductionValueSteps();

    const [name, setName] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
    const [linkedStepIds, setLinkedStepIds] = useState<string[]>([]);

    // DD-3545: linking to workflow steps fills the technician on the production value report
    const isLinkEnabled = isWorkflowValueLinkEnabled(currentOrgVar()?.customerSettings);
    const { workFlow } = useWorkflowSteps();
    const workflowStepOptions = useMemo(
        () => linkableWorkflowSteps(workFlow?.steps, departments, selectedDepartment?._id),
        [workFlow, departments, selectedDepartment],
    );
    const linkedSteps = workflowStepOptions.filter((option) => linkedStepIds.includes(option._id));

    const isEditing = Boolean(step?._id);
    const isSaving = addStepLoading || editStepLoading;

    useEffect(() => {
        if (open) {
            setName(step?.name || '');
            const existingDepartment = step?.departmentId
                ? departments.find((department) => department._id === step.departmentId) ?? null
                : null;
            setSelectedDepartment(existingDepartment);
            setLinkedStepIds(step?.workflowStepIds ?? []);
        }
    }, [open, step, departments]);

    const handleSave = async () => {
        if (!name.trim() || !selectedDepartment?._id) return;

        const stepData = {
            name: name.trim(),
            departmentId: selectedDepartment._id,
            // Left out when the feature is off, so the backend keeps any stored links.
            ...(isLinkEnabled && { workflowStepIds: linkedSteps.map((option) => option._id) }),
        };

        if (isEditing && step?._id) {
            await updateProductionValueStep({ stepId: step._id, step: stepData });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:PRODUCTION_VALUE_STEP_UPDATED_SUCCESSFULLY'),
            });
        } else {
            await addProductionValueStep({ step: stepData });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:PRODUCTION_VALUE_STEP_ADDED_SUCCESSFULLY'),
            });
        }

        onDialogClose();
    };

    return (
        <Dialog open={open} onClose={onDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEditing
                    ? translate('lab-settings:EDIT_PRODUCTION_VALUE_STEP')
                    : translate('lab-settings:ADD_PRODUCTION_VALUE_STEP')}
            </DialogTitle>
            <DialogContent>
                <TextField
                    label={translate('lab-settings:NAME')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    fullWidth
                    required
                    margin="normal"
                    autoFocus
                />
                <Autocomplete
                    options={departments}
                    getOptionLabel={(option) => option.name}
                    value={selectedDepartment}
                    onChange={(_event, newValue) => setSelectedDepartment(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} label={translate('lab-settings:DEPARTMENT')} margin="normal" required />
                    )}
                />
                {isLinkEnabled && (
                    <Autocomplete<LinkableWorkflowStep, true>
                        multiple
                        options={workflowStepOptions}
                        groupBy={(option) => option.departmentName || translate('lab-settings:NO_DEPARTMENT')}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        value={linkedSteps}
                        onChange={(_event, newValue) => setLinkedStepIds(newValue.map((option) => option._id))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={translate('lab-settings:LINKED_WORKFLOW_STEPS')}
                                helperText={translate('lab-settings:LINKED_WORKFLOW_STEPS_HELP')}
                                margin="normal"
                            />
                        )}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogClose} disabled={isSaving}>
                    {translate('common:CANCEL')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!name.trim() || !selectedDepartment || isSaving}
                >
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditProductionValueStepDialog;
